import { CreditCard, Vault, Repeat, ShieldCheck, Network, TrendingUp } from "lucide-react";

const CATEGORY = "Karten & Treasury";

export const TREASURY = [
  {
    slug: "karten",
    name: "Karten",
    category: CATEGORY,
    icon: CreditCard,
    tagline: "Physische und virtuelle Karten, White Label.",
    overline: "Karten",
    hero: {
      title: "Karten, die zu Ihrem Ledger gehören.",
      subtitle:
        "Geben Sie physische und virtuelle Karten unter Ihrer Marke aus – mit granularen Limits, Just-in-Time-Funding und Echtzeit-Autorisierung.",
    },
    stats: [
      { value: "Virtuell", label: "sofort ausgegeben" },
      { value: "JIT", label: "Just-in-Time-Funding" },
      { value: "3DS2", label: "abgesichert" },
    ],
    features: [
      { icon: CreditCard, title: "Virtuell & physisch", desc: "Virtuelle Karten in Sekunden, physische Karten im White-Label-Design." },
      { icon: ShieldCheck, title: "Granulare Limits", desc: "Limits je Karte, Händlerkategorie, Zeitfenster oder Nutzer." },
      { icon: Network, title: "Just-in-Time-Funding", desc: "Autorisierung prüft in Echtzeit das zugrunde liegende Wallet." },
      { icon: Repeat, title: "Echtzeit-Autorisierung", desc: "Eigene Regeln entscheiden pro Transaktion über Freigabe oder Ablehnung." },
    ],
    architecture: {
      caption: "Kartenautorisierung gegen das Ledger in Echtzeit",
      layers: [
        { label: "Netzwerk", nodes: [{ label: "Acquirer", sub: "Scheme" }, { label: "Autorisierung", sub: "ISO" }] },
        { label: "Karten-Engine", nodes: [{ label: "Regeln", sub: "Limits" }, { label: "JIT-Funding", sub: "Wallet" }, { label: "3DS2", sub: "SCA" }] },
        { label: "Ledger", nodes: [{ label: "Reserve", sub: "Hold" }, { label: "Buchung", sub: "Clearing" }] },
      ],
    },
    api: {
      method: "POST",
      endpoint: "/v1/cards",
      code: [
        "// Virtuelle Karte an ein Wallet binden",
        "const card = await cashfux.cards.create({",
        '  wallet: "wlt_b2",',
        '  type: "virtual",',
        "  limits: { daily: 50000 },",
        "});",
        "",
        "card.pan_last4 // 4821",
      ],
      highlights: [
        "Just-in-Time-Funding aus dem Wallet",
        "Autorisierungs-Webhook mit Entscheidung",
        "PCI-DSS Level 1 – keine PAN bei Ihnen",
      ],
    },
    useCases: [
      { title: "Ausgaben-Management", desc: "Mitarbeiterkarten mit Limits und Kategorien." },
      { title: "Plattform-Karten", desc: "Kartenprodukt für Endkunden unter eigener Marke." },
      { title: "Einmal-Karten", desc: "Virtuelle Einmal-Karten für sichere Online-Zahlungen." },
    ],
    faq: [
      { q: "Muss ich PCI-DSS-konform sein?", a: "Sensible Kartendaten bleiben bei CashFUX (PCI-DSS Level 1). Sie arbeiten nur mit Tokens und last4." },
      { q: "Was ist Just-in-Time-Funding?", a: "Bei jeder Autorisierung wird das zugrunde liegende Wallet in Echtzeit geprüft und reserviert." },
      { q: "Kann ich Transaktionen selbst genehmigen?", a: "Ja, über den Autorisierungs-Webhook entscheiden Ihre Regeln pro Transaktion." },
      { q: "Sind physische Karten White-Label?", a: "Ja, physische und virtuelle Karten tragen Ihr Design und Ihre Marke." },
    ],
    related: ["wallets", "treasury", "zahlungen"],
  },
  {
    slug: "treasury",
    name: "Treasury",
    category: CATEGORY,
    icon: Vault,
    tagline: "Liquidität steuern und Salden optimieren.",
    overline: "Treasury",
    hero: {
      title: "Liquidität unter Kontrolle.",
      subtitle:
        "Bündeln Sie Salden, definieren Sie Sweeping-Regeln und steuern Sie Liquidität über Konten und Währungen hinweg – automatisiert und transparent.",
    },
    stats: [
      { value: "Sweeping", label: "regelbasiert" },
      { value: "Pooling", label: "Cash-Pool" },
      { value: "Multi-FX", label: "Salden" },
    ],
    features: [
      { icon: Vault, title: "Cash-Pooling", desc: "Salden mehrerer Konten virtuell oder physisch bündeln." },
      { icon: Repeat, title: "Automatisches Sweeping", desc: "Regelbasierte Umbuchungen zwischen Konten nach Schwellenwerten." },
      { icon: TrendingUp, title: "Liquiditätssicht", desc: "Konsolidierte Echtzeit-Sicht über alle Konten und Währungen." },
      { icon: ShieldCheck, title: "Kontrollen", desc: "Limits, Freigaben und Audit für jede Treasury-Bewegung." },
    ],
    architecture: {
      caption: "Regelbasierte Liquiditätssteuerung über Konten",
      layers: [
        { label: "Konten", nodes: [{ label: "Betrieb", sub: "operativ" }, { label: "Reserve", sub: "Puffer" }, { label: "FX", sub: "Fremdwährung" }] },
        { label: "Treasury-Engine", nodes: [{ label: "Sweeping", sub: "Regeln" }, { label: "Pooling", sub: "virtuell" }, { label: "Schwellen", sub: "Trigger" }] },
        { label: "Ergebnis", nodes: [{ label: "Umbuchung", sub: "Ledger" }, { label: "Report", sub: "Liquidität" }] },
      ],
    },
    api: {
      method: "POST",
      endpoint: "/v1/treasury/rules",
      code: [
        "// Sweeping-Regel definieren",
        "const rule = await cashfux.treasury.rules.create({",
        '  from: "acc_ops",',
        '  to: "acc_reserve",',
        '  trigger: { above: 1000000 },',
        '  keep: 250000, // Zielsaldo im Betriebskonto',
        "});",
      ],
      highlights: [
        "Trigger nach Schwellenwert oder Zeitplan",
        "Virtuelles Pooling ohne echte Umbuchung",
        "Jede Bewegung im Ledger auditierbar",
      ],
    },
    useCases: [
      { title: "Konzern-Treasury", desc: "Liquidität über Tochtergesellschaften bündeln." },
      { title: "Puffersteuerung", desc: "Betriebskonten automatisch auf Zielsalden halten." },
      { title: "FX-Reserven", desc: "Fremdwährungsbestände gezielt disponieren." },
    ],
    faq: [
      { q: "Was ist der Unterschied zwischen Pooling und Sweeping?", a: "Pooling bündelt Salden virtuell zur Ansicht; Sweeping bucht tatsächlich zwischen Konten um." },
      { q: "Wodurch werden Umbuchungen ausgelöst?", a: "Durch Schwellenwerte oder Zeitpläne, definiert als Treasury-Regeln." },
      { q: "Sind Fremdwährungen abgedeckt?", a: "Ja, Salden je Währung fließen in die konsolidierte Liquiditätssicht ein." },
      { q: "Sind Treasury-Bewegungen kontrolliert?", a: "Ja, mit Limits, Freigaben und vollständigem Audit-Trail." },
    ],
    related: ["fx", "wallets", "auszahlungen"],
  },
  {
    slug: "fx",
    name: "FX",
    category: CATEGORY,
    icon: Repeat,
    tagline: "Währungsumrechnung mit garantierten Kursen.",
    overline: "FX",
    hero: {
      title: "Währungen wechseln, ohne Überraschungen.",
      subtitle:
        "Rechnen Sie zwischen Währungen mit transparenten, für einen Moment garantierten Kursen um – als Teil jedes Zahlungs- und Wallet-Flows.",
    },
    stats: [
      { value: "Locked", label: "Kurs-Quote" },
      { value: "30+", label: "Währungspaare" },
      { value: "Transparent", label: "Aufschlag" },
    ],
    features: [
      { icon: Repeat, title: "Garantierte Quotes", desc: "Ein Kurs wird für ein Zeitfenster fixiert und dann atomar ausgeführt." },
      { icon: TrendingUp, title: "Transparente Kurse", desc: "Mittelkurs plus klar ausgewiesener Aufschlag – keine versteckten Spreads." },
      { icon: Network, title: "In jedem Flow", desc: "FX ist in Wallets, Zahlungen und Auszahlungen eingebettet." },
      { icon: ShieldCheck, title: "Atomarer Umtausch", desc: "Umrechnung und Buchung erfolgen in einer unteilbaren Transaktion." },
    ],
    architecture: {
      caption: "Quote-basierter Umtausch im Ledger",
      layers: [
        { label: "Anfrage", nodes: [{ label: "Quote", sub: "Paar" }, { label: "Betrag", sub: "Quelle" }] },
        { label: "FX-Engine", nodes: [{ label: "Kurs", sub: "gesperrt" }, { label: "Aufschlag", sub: "transparent" }, { label: "Ausführung", sub: "atomic" }] },
        { label: "Ergebnis", nodes: [{ label: "Ziel-Wallet", sub: "gutgeschrieben" }, { label: "Ledger", sub: "Buchung" }] },
      ],
    },
    api: {
      method: "POST",
      endpoint: "/v1/fx/quotes",
      code: [
        "// Kurs anfragen und ausführen",
        "const quote = await cashfux.fx.quote({",
        '  sell: "EUR", buy: "USD",',
        "  amount: 100000, // 1.000,00 €",
        "});",
        "",
        "await cashfux.fx.execute({ quote: quote.id });",
        "// Kurs war für 30s garantiert",
      ],
      highlights: [
        "Quote mit fixer Gültigkeitsdauer",
        "Aufschlag explizit im Response",
        "Ausführung atomar mit Ledger-Buchung",
      ],
    },
    useCases: [
      { title: "Cross-Border-Payments", desc: "Zahlungen in Fremdwährung mit klarem Kurs." },
      { title: "Multi-Currency-Wallets", desc: "Guthaben zwischen Währungen umschichten." },
      { title: "Preis-Sicherung", desc: "Kurs für einen Checkout kurzfristig garantieren." },
    ],
    faq: [
      { q: "Wie lange gilt ein Kurs?", a: "Ein Quote ist für ein definiertes Zeitfenster garantiert und wird bei Ausführung fixiert." },
      { q: "Gibt es versteckte Spreads?", a: "Nein. Es gelten Mittelkurs plus ein transparent ausgewiesener Aufschlag." },
      { q: "Ist der Umtausch atomar?", a: "Ja, Umrechnung und Buchung erfolgen in einer unteilbaren Transaktion." },
      { q: "Wo lässt sich FX einsetzen?", a: "In Wallets, Zahlungen und Auszahlungen – überall dort, wo Währungen aufeinandertreffen." },
    ],
    related: ["wallets", "treasury", "zahlungen"],
  },
];
